import { graphqlClient } from '@/graphql/graphql-client';
import { toScalarListVariables } from '@/graphql/list-query';
import { GET_ORG_CONTRACT_DOCUMENT_EVENTS_QUERY } from '@/graphql/queries';
import {
  INSERT_ORG_CONTRACT_DOCUMENT_EVENT_MUTATION,
  UPDATE_ORG_CONTRACT_DOCUMENT_EVENT_MUTATION,
} from '@/graphql/mutations';
import type {
  OrgContractDocumentEventInsertInput,
  OrgContractDocumentEventUpdateInput,
  MutateOrgContractDocumentEventResponse,
  OrgContractDocumentEventItem,
} from '../types/contract-document-event.types';

export interface InsertOrgContractDocumentEventResponse {
  insertOrgContractDocumentEvent: MutateOrgContractDocumentEventResponse & { itemId: string };
}

export interface UpdateOrgContractDocumentEventResponse {
  updateOrgContractDocumentEvent: MutateOrgContractDocumentEventResponse;
}

export interface GetContractDocumentEventsResult {
  items: OrgContractDocumentEventItem[];
  totalCount: number;
}

// Strip out fields the gateway can't accept (empty DateTime triggers a server
// error per the project's UDS rules). Keep required fields explicit so the
// returned shape still satisfies OrgContractDocumentEventInsertInput.
const cleanInsertInput = (
  input: OrgContractDocumentEventInsertInput
): OrgContractDocumentEventInsertInput => {
  const { ContractDocumentId, OrganizationId, EventType, ...optional } = input;
  const cleanedOptional: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(optional)) {
    if (value === undefined || value === null) continue;
    if ((key === 'OccurredAt' || key === 'SignedTime') && value === '') continue;
    cleanedOptional[key] = value;
  }
  return { ContractDocumentId, OrganizationId, EventType, ...cleanedOptional };
};

export const createContractDocumentEvent = async (
  input: OrgContractDocumentEventInsertInput
): Promise<InsertOrgContractDocumentEventResponse> => {
  const response = await graphqlClient.request(INSERT_ORG_CONTRACT_DOCUMENT_EVENT_MUTATION, cleanInsertInput(input));

  const result = response.insertOrgContractDocumentEvent;
  if (!result?.itemId) {
    throw new Error('No itemId returned from event insert');
  }
  return { insertOrgContractDocumentEvent: { ...result, itemId: result.itemId } };
};

export const updateContractDocumentEvent = async (
  itemId: string,
  input: OrgContractDocumentEventUpdateInput
): Promise<UpdateOrgContractDocumentEventResponse> => {
  const response = await graphqlClient.request(UPDATE_ORG_CONTRACT_DOCUMENT_EVENT_MUTATION, { ItemId: itemId, ...input });

  return response;
};

export const getContractDocumentEventsByContractId = async (
  contractDocumentId: string
): Promise<GetContractDocumentEventsResult> => {
  if (!contractDocumentId) return { items: [], totalCount: 0 };

  try {
    const response = await graphqlClient.query({
      query: GET_ORG_CONTRACT_DOCUMENT_EVENTS_QUERY,
      variables: toScalarListVariables({
        where: { ContractDocumentId: { eq: contractDocumentId } },
        paging: { pageNo: 1, pageSize: 200 },
      }, { contractDocumentId: 'ContractDocumentId' }),
    });

    const result = response?.getOrgContractDocumentEvents;
    return {
      items: result?.items ?? [],
      totalCount: result?.totalCount ?? 0,
    };
  } catch (error) {
    console.error('Error fetching contract document events:', error);
    return { items: [], totalCount: 0 };
  }
};
